import Image from "next/image";
import React from "react";

export interface WTwoColumnSectionProps {
  imageUrl?: string;
  imageAlt?: string;
  imageOnLeft?: boolean;
  heading?: string;
  points?: string[];
}

function TickIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

/**
 * W-Two Column Section
 * Left or right image, opposite side: title + tick-mark points.
 */
export default function WTwoColumnSection({
  imageUrl,
  imageAlt,
  imageOnLeft = true,
  heading,
  points = [],
}: WTwoColumnSectionProps) {
  const hasImage = Boolean(imageUrl && imageUrl.trim() !== "");
  const hasContent = Boolean(
    (heading && heading.trim() !== "") || points.length > 0
  );
  if (!hasImage && !hasContent) return null;

  const imageBlock = hasImage && (
    <div className="w-full">
      <Image
        src={imageUrl!}
        alt={imageAlt || heading || "Section image"}
        width={800}
        height={600}
        className="w-full h-auto rounded-xl object-contain"
        sizes="(min-width: 768px) 50vw, 100vw"
        unoptimized={
          imageUrl!.startsWith("http") && !imageUrl!.includes("trichos")
        }
      />
    </div>
  );

  const rightColumnContent = (
    <div className="flex flex-col justify-center">
      {heading && heading.trim() !== "" && (
        <h2 className="mb-4 text-[30px] font-bold uppercase text-violet-950">
          {heading.trim()}
        </h2>
      )}
      {points.length > 0 && (
        <ul className="space-y-3" role="list">
          {points
            .filter((p) => p != null && String(p).trim() !== "")
            .map((point, index) => (
              <li
                key={index}
                className="flex items-start gap-3 text-gray-700 md:text-lg"
              >
                <span className="mt-0.5 shrink-0 text-violet-950" aria-hidden>
                  <TickIcon className="size-5" />
                </span>
                <span>{String(point).trim()}</span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="grid gap-8 md:grid-cols-2 md:items-center md:gap-12">
          {imageOnLeft ? (
            <>
              {imageBlock}
              {rightColumnContent}
            </>
          ) : (
            <>
              {rightColumnContent}
              {imageBlock}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
